import { createAction, props } from '@ngrx/store';

import { DoctorsFilter, LocationsFilter, OfferingsFilter, PartnersFilter } from '@admin/src/core/models/partner-state.interface';

import { InfiniteScrollResponse } from '@lib/core/models/infinite-scroll-response.interface';
import {
  DoctorPayloadV2,
  DoctorSearchModelV2,
  DoctorV2,
  LocationPayloadV2,
  LocationSearchModelV2,
  LocationV2,
  OfferingPayloadV2,
  OfferingSearchModelV2,
  OfferingV2,
  PartnerPayloadV2,
  PartnerSearchModelV2,
  PartnerV2,
} from '@lib/core/models/partners.interface';

// Set Partners Filters
export const setPartnersFilter = createAction('[Partner] Set Partners Filter', props<{ filter: PartnersFilter }>());

// Fetching Partners By Specific Method
export const fetchPartnersFirstPage = createAction('[Partner] Fetch Partners First Page');
export const fetchPartnersNextPage = createAction('[Partner] Fetch Partners Next Page');

// Get Partners List (internal action used by effects)
export const getPartnersList = createAction('[Partner] Get Partners List', props<{ payload: PartnerSearchModelV2 }>());
export const getPartnersListSuccess = createAction('[Partner] Get Partners List Success', props<{ response: InfiniteScrollResponse<PartnerV2> }>());
export const getPartnersListFailure = createAction('[Partner] Get Partners List Failure', props<{ error: any }>());

// Get Partner By Id
export const getPartnerById = createAction('[Partner] Get Partner By Id', props<{ id: string }>());
export const getPartnerByIdSuccess = createAction('[Partner] Get Partner By Id Success', props<{ partner: PartnerV2 }>());
export const getPartnerByIdFailure = createAction('[Partner] Get Partner By Id Failure', props<{ error: any }>());

// Create Partner
export const createPartner = createAction('[Partner] Create Partner', props<{ payload: PartnerPayloadV2; selectedLogo: File | null }>());
export const createPartnerSuccess = createAction('[Partner] Create Partner Success', props<{ partner: PartnerV2 }>());
export const createPartnerFailure = createAction('[Partner] Create Partner Failure', props<{ error: any }>());

// Edit Partner
export const editPartner = createAction('[Partner] Edit Partner', props<{ payload: PartnerPayloadV2 }>());
export const editPartnerSuccess = createAction('[Partner] Edit Partner Success', props<{ partner: PartnerV2 }>());
export const editPartnerFailure = createAction('[Partner] Edit Partner Failure', props<{ error: any }>());

// Activate Partner
export const activatePartner = createAction('[Partner] Activate Partner', props<{ id: string }>());
export const activatePartnerSuccess = createAction('[Partner] Activate Partner Success', props<{ partner: PartnerV2 }>());
export const activatePartnerFailure = createAction('[Partner] Activate Partner Failure', props<{ error: any }>());

// Deactivate Partner
export const deactivatePartner = createAction('[Partner] Deactivate Partner', props<{ id: string }>());
export const deactivatePartnerSuccess = createAction('[Partner] Deactivate Partner Success', props<{ partner: PartnerV2 }>());
export const deactivatePartnerFailure = createAction('[Partner] Deactivate Partner Failure', props<{ error: any }>());

// Set Partner Available
export const setPartnerAvailable = createAction('[Partner] Set Partner Available', props<{ id: string }>());
export const setPartnerAvailableSuccess = createAction('[Partner] Set Partner Available Success', props<{ partner: PartnerV2 }>());
export const setPartnerAvailableFailure = createAction('[Partner] Set Partner Available Failure', props<{ error: any }>());

// Set Partner Unavailable
export const setPartnerUnavailable = createAction('[Partner] Set Partner Unavailable', props<{ id: string }>());
export const setPartnerUnavailableSuccess = createAction('[Partner] Set Partner Unavailable Success', props<{ partner: PartnerV2 }>());
export const setPartnerUnavailableFailure = createAction('[Partner] Set Partner Unavailable Failure', props<{ error: any }>());

// Delete Partner
export const deletePartner = createAction('[Partner] Delete Partner', props<{ id: string }>());
export const deletePartnerSuccess = createAction('[Partner] Delete Partner Success', props<{ id: string }>());
export const deletePartnerFailure = createAction('[Partner] Delete Partner Failure', props<{ error: any }>());

// Upload Partner Logo
export const uploadPartnerLogo = createAction('[Partner] Upload Partner Logo', props<{ partnerId: string; file: File }>());
export const uploadPartnerLogoSuccess = createAction('[Partner] Upload Partner Logo Success', props<{ partner: PartnerV2 }>());
export const uploadPartnerLogoFailure = createAction('[Partner] Upload Partner Logo Failure', props<{ error: any }>());

// Clear State
export const clearPartnersList = createAction('[Partner] Clear Partners List');
export const clearCurrentPartner = createAction('[Partner] Clear Current Partner');

// ===== LOCATION ACTIONS =====

// Set Locations Filter
export const setLocationsFilter = createAction('[Location] Set Locations Filter', props<{ filter: LocationsFilter }>());

// Fetching Locations By Specific Method
export const fetchLocationsFirstPage = createAction('[Location] Fetch Locations First Page', props<{ partnerId: string }>());
export const fetchLocationsNextPage = createAction('[Location] Fetch Locations Next Page', props<{ partnerId: string }>());

// Get Locations List (internal action used by effects)
export const getLocationsList = createAction('[Location] Get Locations List', props<{ payload: LocationSearchModelV2 }>());
export const getLocationsListSuccess = createAction('[Location] Get Locations List Success', props<{ response: InfiniteScrollResponse<LocationV2> }>());
export const getLocationsListFailure = createAction('[Location] Get Locations List Failure', props<{ error: any }>());

// Get Location By Id
export const getLocationById = createAction('[Location] Get Location By Id', props<{ id: string }>());
export const getLocationByIdSuccess = createAction('[Location] Get Location By Id Success', props<{ location: LocationV2 }>());
export const getLocationByIdFailure = createAction('[Location] Get Location By Id Failure', props<{ error: any }>());

// Create Location
export const createLocation = createAction('[Location] Create Location', props<{ payload: LocationPayloadV2; selectedLogo: File | null }>());
export const createLocationSuccess = createAction('[Location] Create Location Success', props<{ location: LocationV2 }>());
export const createLocationFailure = createAction('[Location] Create Location Failure', props<{ error: any }>());

// Edit Location
export const editLocation = createAction('[Location] Edit Location', props<{ payload: LocationPayloadV2 }>());
export const editLocationSuccess = createAction('[Location] Edit Location Success', props<{ location: LocationV2 }>());
export const editLocationFailure = createAction('[Location] Edit Location Failure', props<{ error: any }>());

// Activate Location
export const activateLocation = createAction('[Location] Activate Location', props<{ id: string }>());
export const activateLocationSuccess = createAction('[Location] Activate Location Success', props<{ location: LocationV2 }>());
export const activateLocationFailure = createAction('[Location] Activate Location Failure', props<{ error: any }>());

// Deactivate Location
export const deactivateLocation = createAction('[Location] Deactivate Location', props<{ id: string }>());
export const deactivateLocationSuccess = createAction('[Location] Deactivate Location Success', props<{ location: LocationV2 }>());
export const deactivateLocationFailure = createAction('[Location] Deactivate Location Failure', props<{ error: any }>());

// Set Location Available
export const setLocationAvailable = createAction('[Location] Set Location Available', props<{ id: string }>());
export const setLocationAvailableSuccess = createAction('[Location] Set Location Available Success', props<{ location: LocationV2 }>());
export const setLocationAvailableFailure = createAction('[Location] Set Location Available Failure', props<{ error: any }>());

// Set Location Unavailable
export const setLocationUnavailable = createAction('[Location] Set Location Unavailable', props<{ id: string }>());
export const setLocationUnavailableSuccess = createAction('[Location] Set Location Unavailable Success', props<{ location: LocationV2 }>());
export const setLocationUnavailableFailure = createAction('[Location] Set Location Unavailable Failure', props<{ error: any }>());

// Delete Location
export const deleteLocation = createAction('[Location] Delete Location', props<{ id: string }>());
export const deleteLocationSuccess = createAction('[Location] Delete Location Success', props<{ id: string }>());
export const deleteLocationFailure = createAction('[Location] Delete Location Failure', props<{ error: any }>());

// Upload Location Logo
export const uploadLocationLogo = createAction('[Location] Upload Location Logo', props<{ partnerId: string; locationId: string; file: File }>());
export const uploadLocationLogoSuccess = createAction('[Location] Upload Location Logo Success', props<{ location: LocationV2 }>());
export const uploadLocationLogoFailure = createAction('[Location] Upload Location Logo Failure', props<{ error: any }>());

// Clear Location State
export const clearLocationsList = createAction('[Location] Clear Locations List');
export const clearCurrentLocation = createAction('[Location] Clear Current Location');

// ===== DOCTOR ACTIONS =====

// Set Doctors Filter
export const setDoctorsFilter = createAction('[Doctor] Set Doctors Filter', props<{ filter: DoctorsFilter }>());

// Fetching Doctors By Specific Method
export const fetchDoctorsFirstPage = createAction('[Doctor] Fetch Doctors First Page', props<{ partnerId: string }>());
export const fetchDoctorsNextPage = createAction('[Doctor] Fetch Doctors Next Page', props<{ partnerId: string }>());

// Get Doctors List (internal action used by effects)
export const getDoctorsList = createAction('[Doctor] Get Doctors List', props<{ payload: DoctorSearchModelV2 }>());
export const getDoctorsListSuccess = createAction('[Doctor] Get Doctors List Success', props<{ response: InfiniteScrollResponse<DoctorV2> }>());
export const getDoctorsListFailure = createAction('[Doctor] Get Doctors List Failure', props<{ error: any }>());

// Get Doctor By Id
export const getDoctorById = createAction('[Doctor] Get Doctor By Id', props<{ id: string }>());
export const getDoctorByIdSuccess = createAction('[Doctor] Get Doctor By Id Success', props<{ doctor: DoctorV2 }>());
export const getDoctorByIdFailure = createAction('[Doctor] Get Doctor By Id Failure', props<{ error: any }>());

// Create Doctor
export const createDoctor = createAction('[Doctor] Create Doctor', props<{ payload: DoctorPayloadV2 }>());
export const createDoctorSuccess = createAction('[Doctor] Create Doctor Success', props<{ doctor: DoctorV2 }>());
export const createDoctorFailure = createAction('[Doctor] Create Doctor Failure', props<{ error: any }>());

// Edit Doctor
export const editDoctor = createAction('[Doctor] Edit Doctor', props<{ payload: DoctorPayloadV2 }>());
export const editDoctorSuccess = createAction('[Doctor] Edit Doctor Success', props<{ doctor: DoctorV2 }>());
export const editDoctorFailure = createAction('[Doctor] Edit Doctor Failure', props<{ error: any }>());

// Activate Doctor
export const activateDoctor = createAction('[Doctor] Activate Doctor', props<{ id: string }>());
export const activateDoctorSuccess = createAction('[Doctor] Activate Doctor Success', props<{ doctor: DoctorV2 }>());
export const activateDoctorFailure = createAction('[Doctor] Activate Doctor Failure', props<{ error: any }>());

// Deactivate Doctor
export const deactivateDoctor = createAction('[Doctor] Deactivate Doctor', props<{ id: string }>());
export const deactivateDoctorSuccess = createAction('[Doctor] Deactivate Doctor Success', props<{ doctor: DoctorV2 }>());
export const deactivateDoctorFailure = createAction('[Doctor] Deactivate Doctor Failure', props<{ error: any }>());

// Set Doctor Available
export const setDoctorAvailable = createAction('[Doctor] Set Doctor Available', props<{ id: string }>());
export const setDoctorAvailableSuccess = createAction('[Doctor] Set Doctor Available Success', props<{ doctor: DoctorV2 }>());
export const setDoctorAvailableFailure = createAction('[Doctor] Set Doctor Available Failure', props<{ error: any }>());

// Set Doctor Unavailable
export const setDoctorUnavailable = createAction('[Doctor] Set Doctor Unavailable', props<{ id: string }>());
export const setDoctorUnavailableSuccess = createAction('[Doctor] Set Doctor Unavailable Success', props<{ doctor: DoctorV2 }>());
export const setDoctorUnavailableFailure = createAction('[Doctor] Set Doctor Unavailable Failure', props<{ error: any }>());

// Delete Doctor
export const deleteDoctor = createAction('[Doctor] Delete Doctor', props<{ id: string }>());
export const deleteDoctorSuccess = createAction('[Doctor] Delete Doctor Success', props<{ id: string }>());
export const deleteDoctorFailure = createAction('[Doctor] Delete Doctor Failure', props<{ error: any }>());

// Clear Doctor State
export const clearDoctorsList = createAction('[Doctor] Clear Doctors List');
export const clearCurrentDoctor = createAction('[Doctor] Clear Current Doctor');

// ===== OFFERING ACTIONS =====

// Set Offerings Filter
export const setOfferingsFilter = createAction('[Offering] Set Offerings Filter', props<{ filter: OfferingsFilter }>());

// Fetching Offerings By Specific Method
export const fetchOfferingsFirstPage = createAction('[Offering] Fetch Offerings First Page', props<{ partnerId: string }>());
export const fetchOfferingsNextPage = createAction('[Offering] Fetch Offerings Next Page', props<{ partnerId: string }>());

// Get Offerings List (internal action used by effects)
export const getOfferingsList = createAction('[Offering] Get Offerings List', props<{ payload: OfferingSearchModelV2 }>());
export const getOfferingsListSuccess = createAction('[Offering] Get Offerings List Success', props<{ response: InfiniteScrollResponse<OfferingV2> }>());
export const getOfferingsListFailure = createAction('[Offering] Get Offerings List Failure', props<{ error: any }>());

// Get Offering By Id
export const getOfferingById = createAction('[Offering] Get Offering By Id', props<{ id: string }>());
export const getOfferingByIdSuccess = createAction('[Offering] Get Offering By Id Success', props<{ offering: OfferingV2 }>());
export const getOfferingByIdFailure = createAction('[Offering] Get Offering By Id Failure', props<{ error: any }>());

// Create Offering
export const createOffering = createAction('[Offering] Create Offering', props<{ payload: OfferingPayloadV2 }>());
export const createOfferingSuccess = createAction('[Offering] Create Offering Success', props<{ offering: OfferingV2 }>());
export const createOfferingFailure = createAction('[Offering] Create Offering Failure', props<{ error: any }>());

// Edit Offering
export const editOffering = createAction('[Offering] Edit Offering', props<{ payload: OfferingPayloadV2 }>());
export const editOfferingSuccess = createAction('[Offering] Edit Offering Success', props<{ offering: OfferingV2 }>());
export const editOfferingFailure = createAction('[Offering] Edit Offering Failure', props<{ error: any }>());

// Activate Offering
export const activateOffering = createAction('[Offering] Activate Offering', props<{ id: string }>());
export const activateOfferingSuccess = createAction('[Offering] Activate Offering Success', props<{ offering: OfferingV2 }>());
export const activateOfferingFailure = createAction('[Offering] Activate Offering Failure', props<{ error: any }>());

// Deactivate Offering
export const deactivateOffering = createAction('[Offering] Deactivate Offering', props<{ id: string }>());
export const deactivateOfferingSuccess = createAction('[Offering] Deactivate Offering Success', props<{ offering: OfferingV2 }>());
export const deactivateOfferingFailure = createAction('[Offering] Deactivate Offering Failure', props<{ error: any }>());

// Set Offering Available
export const setOfferingAvailable = createAction('[Offering] Set Offering Available', props<{ id: string }>());
export const setOfferingAvailableSuccess = createAction('[Offering] Set Offering Available Success', props<{ offering: OfferingV2 }>());
export const setOfferingAvailableFailure = createAction('[Offering] Set Offering Available Failure', props<{ error: any }>());

// Set Offering Unavailable
export const setOfferingUnavailable = createAction('[Offering] Set Offering Unavailable', props<{ id: string }>());
export const setOfferingUnavailableSuccess = createAction('[Offering] Set Offering Unavailable Success', props<{ offering: OfferingV2 }>());
export const setOfferingUnavailableFailure = createAction('[Offering] Set Offering Unavailable Failure', props<{ error: any }>());

// Delete Offering
export const deleteOffering = createAction('[Offering] Delete Offering', props<{ id: string }>());
export const deleteOfferingSuccess = createAction('[Offering] Delete Offering Success', props<{ id: string }>());
export const deleteOfferingFailure = createAction('[Offering] Delete Offering Failure', props<{ error: any }>());

// Clear Offering State
export const clearOfferingsList = createAction('[Offering] Clear Offerings List');
export const clearCurrentOffering = createAction('[Offering] Clear Current Offering');
